import React from 'react';
import { ChevronRight, Folder } from 'lucide-react';
import type { WorkspaceFolder } from '../../utils/workspaceFolders';
import { formatCost, formatRelativeTime, pluralize } from '../../utils/workspaceLibrary';
import { LibraryMenu, type MenuEntry } from './LibraryMenu';
import type { DropTargetHandlers } from './ProjectCard';
import type { FolderColor } from '../../utils/workspaceFolders';
import { WorkspaceThumb } from './ProjectTile';

export interface FolderTileProps {
    folder: WorkspaceFolder;
    /** Colour of the owning project (folders inherit it). */
    color?: FolderColor;
    workspaceCount: number;
    folderCount: number;
    /** What its workspaces' generations cost together, in US dollars */
    totalCost?: number;
    /** The newest pictures inside it, a strip under the name */
    previews?: string[];
    editedAt?: number;
    onOpen: () => void;
    menuItems: MenuEntry[];
    drop?: DropTargetHandlers;
    draggable?: boolean;
    onDragStart?: (e: React.DragEvent) => void;
    onDragEnd?: () => void;
    dragging?: boolean;
}

/** A folder as a card: the folder mark, its name, what is in it and when, and a strip of its newest pictures. */
export const FolderTile: React.FC<FolderTileProps> = ({ folder, workspaceCount, folderCount, totalCost, previews = [], editedAt, onOpen, menuItems, drop, draggable, onDragStart, onDragEnd, dragging }) => {
    const summary = workspaceCount === 0 && folderCount === 0
        ? 'Empty'
        : [
            workspaceCount > 0 ? pluralize(workspaceCount, 'workspace') : null,
            folderCount > 0 ? pluralize(folderCount, 'folder') : null,
            totalCost && totalCost > 0 ? formatCost(totalCost) : null,
            editedAt ? formatRelativeTime(editedAt) : null,
        ].filter(Boolean).join(' · ');

    return (
        <div
            role="listitem"
            className={`ns-lib-card ns-lib-card-folder ${drop?.active ? 'ns-lib-drop' : ''}`}
            data-dragging={dragging ? 'true' : undefined}
            draggable={draggable}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onDragOver={drop?.onDragOver}
            onDragLeave={drop?.onDragLeave}
            onDrop={drop?.onDrop}
            data-testid={`folder-tile-${folder.id}`}
        >
            <button
                type="button"
                className="flex flex-col gap-2.5 w-full min-w-0 text-left p-3 pr-10 outline-none focus-visible:ring-4 focus-visible:ring-lavender-soft rounded-2xl"
                onClick={onOpen}
                aria-label={`Open folder ${folder.name}`}
            >
                <span className="flex items-center gap-3 min-w-0">
                    <span className="ns-mark ns-mark-sm ns-mark-lavender" aria-hidden="true"><Folder /></span>
                    <span className="min-w-0 flex-1">
                        <span className="block text-sm font-semibold text-ink truncate" title={folder.name}>{folder.name}</span>
                        <span className="block ns-t-small truncate">{summary}</span>
                    </span>
                    <ChevronRight className="w-4 h-4 text-ink-faint flex-none" aria-hidden="true" />
                </span>
                {previews.length > 0 && (
                    <span className="flex gap-1" aria-hidden="true">
                        {previews.slice(0, 5).map((src, i) => <WorkspaceThumb key={i} thumbnail={src} px={36} className="rounded-lg" />)}
                    </span>
                )}
            </button>
            <div className="absolute top-2 right-2">
                <LibraryMenu label={`More actions for ${folder.name}`} items={menuItems} triggerClassName="ns-icon-btn ns-icon-btn-sm ns-lib-card-reveal ns-lib-card-reveal-soft" />
            </div>
        </div>
    );
};
